import { createSlice, PayloadAction, createAsyncThunk, createSelector } from '@reduxjs/toolkit';

import { TeacherClassStudentFragment } from '@school_ify/schoolify-repository/src/teacher/types';
import {
  ChatUserInsertInput,
  ChatFragment,
  ChatInsertByTypeGroupMutationVariables,
} from '@school_ify/schoolify-repository/src/shared/types';
import { StudentOrTeacherChatUserInsertInput } from '@school_ify/schoolify-repository/src/student_teacher/types';

import ChatService from 'domain/services/chat/ChatServices';

export const addConversationIdToParticipants = (participants, chat_id) =>
  participants.map(e => {
    const newPart = {
      user_id: e.user_id,
      chat_id,
    };
    return newPart;
  });

const addParticipantsToGroupChat = async (data: StudentOrTeacherChatUserInsertInput[]) => {
  try {
    await ChatService.addParticipantToGroupdChat({ participants: data });
  } catch (error) {
    throw new Error(error);
  }
};

export const togglePinMessage = async (data: any) => {
  try {
    await ChatService.pinMessages(data);
  } catch (error) {
    throw new Error(error);
  }
};

export const fetchGroupChats = createAsyncThunk('groupMessages/fetchGroupChats', async () => {
  try {
    const chats = await ChatService.getGroupChats();
    return chats;
  } catch (error) {
    throw new Error(error);
  }
});

export const createGroupConversation = createAsyncThunk(
  'groupMessages/createGroupConversation',
  async (
    payload: {
      data: ChatInsertByTypeGroupMutationVariables;
      participants: ChatUserInsertInput[];
    },
    { dispatch }
  ) => {
    try {
      const { data, participants } = payload;
      const chat = await ChatService.createTeacherGroupChat(data);
      const normalizeParticipants = addConversationIdToParticipants(participants, chat.id);
      await addParticipantsToGroupChat(normalizeParticipants);
      dispatch(fetchGroupChats());
    } catch (error) {
      throw new Error(error);
    }
  }
);

export const fetchGroupChatPossibleParticipants = createAsyncThunk(
  'groupMessages/fetchGroupChatPossibleParticipants',
  async () => {
    try {
      const participants = await ChatService.getGroupChatPossibleParticipants();
      return participants;
    } catch (error) {
      throw new Error(error);
    }
  }
);

interface GroupMessageState {
  error: string;
  loading: 'idle' | 'pending' | 'success' | 'failed';
  groupChats: ChatFragment[];
  loadingPossibleParticipants: 'idle' | 'pending' | 'success' | 'failed';
  possibleParticipants: TeacherClassStudentFragment[];
}

interface UnreadMessageType {
  body: string;
  id: string;
  user_id: string;
  chat_id: string;
}
const initialState = {
  loading: 'idle',
  groupChats: [],
  possibleParticipants: [],
} as GroupMessageState;

const groupMessagesSlice = createSlice({
  name: 'directMessages',
  initialState,
  reducers: {
    updateUnreadMessageCount(state, action: PayloadAction<UnreadMessageType>) {
      state.groupChats = state.groupChats.map(e => {
        if (e.id === action.payload.chat_id) {
          e.unread_messages += 1;
        }
        return e;
      });
    },
    markAllMassagesAsRead(state, action: PayloadAction<string>) {
      state.groupChats = state.groupChats.map(e => {
        if (e.id === action.payload) {
          e.unread_messages = 0;
        }
        return e;
      });
    },
    updateGroupChats(state, action) {
      state.groupChats = action.payload.chats;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchGroupChats.pending, state => {
      state.loading = 'pending';
    });
    builder.addCase(fetchGroupChats.fulfilled, (state, action) => {
      state.loading = 'success';
      state.groupChats = action.payload;
    });
    builder.addCase(fetchGroupChats.rejected, (state, action) => {
      state.loading = 'failed';
      state.error = action.error.message;
    });
    builder.addCase(createGroupConversation.pending, state => {
      state.loading = 'pending';
    });
    builder.addCase(createGroupConversation.fulfilled, state => {
      state.loading = 'success';
    });
    builder.addCase(createGroupConversation.rejected, (state, action) => {
      state.loading = 'failed';
      state.error = action.error.message;
    });
    builder.addCase(fetchGroupChatPossibleParticipants.pending, state => {
      state.loadingPossibleParticipants = 'pending';
    });
    builder.addCase(fetchGroupChatPossibleParticipants.fulfilled, (state, action) => {
      state.loadingPossibleParticipants = 'success';
      state.possibleParticipants = action.payload;
    });
    builder.addCase(fetchGroupChatPossibleParticipants.rejected, (state, action) => {
      state.loadingPossibleParticipants = 'failed';
      state.error = action.error.message;
    });
  },
});

const selectState = state => state.group_messages;

export const selectGroupChats = createSelector(selectState, group_messages => {
  return {
    groupChats: group_messages.groupChats,
    loading: group_messages.loading,
  };
});

export const { updateUnreadMessageCount, markAllMassagesAsRead, updateGroupChats } =
  groupMessagesSlice.actions;
export default groupMessagesSlice.reducer;
